import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../../api";
import "../../views/BookingFlow/BookingFlow.css";
import ConciergeLogo from "../../assets/images/conciergeLogoLight.svg";

const SuccessScreen = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [successData, setSuccessData] = useState(null);
  const [error, setError] = useState(null);

  const sessionId = searchParams.get("session_id");
  const type = searchParams.get("type");
  const cartId = searchParams.get("cart_id");

  useEffect(() => {
    if (sessionId && type && cartId) {
      handleSuccess();
    } else {
      setError("Missing required parameters");
      setLoading(false);
    }
  }, [sessionId, type, cartId]);

  const handleSuccess = async () => {
    try {
      // Verify the Stripe session
      const response = await api("booking-flow/verify-purchase", "POST", {
        sessionId,
        type,
        cartId,
      });

      if (response.status === 200) {
        setSuccessData(response.data);
      } else {
        setError("Failed to verify purchase");
      }
    } catch (error) {
      console.error("Error verifying purchase:", error);
      setError("Error verifying purchase");
    } finally {
      setLoading(false);
    }
  };

  const getSuccessMessage = () => {
    if (!successData) return "Purchase successful!";

    switch (type) {
      case "extra":
        return `Extra "${successData.itemName}" purchased successfully!`;
      case "subscription":
        return `Subscription "${successData.itemName}" activated successfully!`;
      case "gift-card":
        return `$${successData.amount} Gift Card purchased successfully!`;
      default:
        return "Purchase successful!";
    }
  };

  const getSuccessDescription = () => {
    if (!successData) return "Thank you for your purchase.";

    switch (type) {
      case "extra":
        return "Your extra service has been added to your booking and will be provided on the day.";
      case "subscription":
        return "Your subscription is now active and ready to use for future bookings.";
      case "gift-card":
        return `A gift card for $${successData.amount} has been sent to ${
          successData.recipient || "the recipient"
        }.`;
      default:
        return "Thank you for your purchase.";
    }
  };

  if (loading) {
    return (
      <div className="success-container">
        <div className="success-content">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Verifying your purchase...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="success-container">
        <div className="success-content">
          <div className="error-message">
            <h2>❌ Error</h2>
            <p>{error}</p>
            <button
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="success-container">
      <div className="success-content">
        {/* Header */}
        <div className="success-header">
          <div className="logo">
            <img
              src={ConciergeLogo}
              alt="Concierge Car Wash"
              className="logo-image"
            />
          </div>
        </div>

        {/* Success Message */}
        <div className="success-message">
          <div className="success-icon">✅</div>
          <h2 className="success-title">{getSuccessMessage()}</h2>
          <p className="success-description">{getSuccessDescription()}</p>
        </div>

        {/* Purchase Details */}
        {successData && (
          <div className="purchase-details">
            <h3>Purchase Details</h3>
            <div className="detail-item">
              <span className="detail-label">Transaction ID:</span>
              <span className="detail-value">
                {successData.transactionId || "N/A"}
              </span>
            </div>
            {successData.itemName && (
              <div className="detail-item">
                <span className="detail-label">Item:</span>
                <span className="detail-value">{successData.itemName}</span>
              </div>
            )}
            {successData.amount && (
              <div className="detail-item">
                <span className="detail-label">Amount:</span>
                <span className="detail-value">${successData.amount}</span>
              </div>
            )}
            {successData.recipient && (
              <div className="detail-item">
                <span className="detail-label">Recipient:</span>
                <span className="detail-value">{successData.recipient}</span>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="action-buttons">
          <button
            className="back-to-booking-button"
            onClick={() => {
              // Try to get booking ID from URL or redirect to home
              const urlParams = new URLSearchParams(window.location.search);
              const bookingId = urlParams.get("booking_id");
              if (bookingId) {
                navigate(`/booking-extras/${bookingId}`);
              } else {
                navigate("/");
              }
            }}
          >
            Add Extras
          </button>
          <button className="home-button" onClick={() => navigate("/")}>
            Go Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuccessScreen;
